// Carte du monde : une image en pixel art dessinée dans l'administration, et la zone de chaque carte du jeu
// sur elle (un rectangle pour une ville ou un lieu, un groupe de pixels pour une route ; un bâtiment n'y figure pas).

import type { MapKind, Warp } from './admin-api.ts';

/** Pas de plus grand côté ni de poids maximal : une image importée est gardée telle quelle. */
export const WORLD_MAP_LIMITS = { minSide: 16 } as const;
/** Taille du joueur sur la carte du monde, en pixels de l'image (null : 14 px à l'écran, quelle que soit l'image). */
export const WORLD_MAP_PLAYER_SIZE = { min: 0.5, step: 0.5 } as const;
export const WORLD_MAP_DEFAULT_SIZE = { width: 160, height: 120 } as const;

/** Image de la carte du monde pour les joueurs, servie à part (`?v=` : sa version, que le navigateur garde en cache). */
export const WORLD_MAP_IMAGE_URL = '/api/world-map/image';

/** Zone d'une carte du jeu sur la carte du monde, en pixels de l'image. */
export type WorldMapArea =
  | { mapId: number; kind: 'town' | 'place'; x: number; y: number; width: number; height: number }
  | { mapId: number; kind: 'route'; pixels: [number, number][] };

export interface WorldMap {
  width: number;
  height: number;
  /** Image PNG en data URL ; null tant que rien n'a été dessiné. */
  image: string | null;
  /** Diamètre du repère du joueur, en pixels de l'image ; null : taille par défaut (14 px à l'écran). */
  playerSize: number | null;
  areas: WorldMapArea[];
}

/** Ce que voit le joueur en ouvrant la carte du monde. */
export interface WorldMapView extends Omit<WorldMap, 'areas' | 'image'> {
  /** Adresse de l'image (WORLD_MAP_IMAGE_URL, versionnée) ; null tant que rien n'a été dessiné. */
  image: string | null;
  /** `visited` : le joueur est déjà allé sur cette carte (sinon, elle est grisée). */
  areas: (WorldMapArea & { name: string; visited: boolean })[];
  /** Carte où se trouve le joueur. */
  here: string;
  /**
   * Où dessiner le joueur (en pixels de l'image, décimaux) : dans la zone de sa carte, sinon au centre de celle
   * de la carte placée la plus proche par les passages (un intérieur prend la zone du village d'où l'on entre) ;
   * null s'il n'y en a aucune.
   */
  position: { mapId: number; x: number; y: number } | null;
  /**
   * Le Vol : `allowed`, le joueur peut s'envoler vers une ville déjà visitée (clic sur son rectangle) ; sinon `missing`
   * dit ce qui lui manque. null : le Vol est désactivé.
   */
  fly: { allowed: boolean; missing: string } | null;
}

/** Ville et lieu sont des rectangles ; seule une route est faite de pixels. */
export const isRectArea = (area: WorldMapArea): area is Extract<WorldMapArea, { kind: 'town' | 'place' }> => area.kind !== 'route';

/** Le pixel (x, y) est-il dans cette zone en rectangle ? Un lieu est l'ellipse inscrite dans son rectangle. */
export function inRectArea(area: { kind: 'town' | 'place'; x: number; y: number; width: number; height: number }, x: number, y: number): boolean {
  if (x < area.x || y < area.y || x >= area.x + area.width || y >= area.y + area.height) return false;
  if (area.kind === 'town') return true;
  const dx = (x + 0.5 - area.x) / area.width * 2 - 1, dy = (y + 0.5 - area.y) / area.height * 2 - 1;
  return dx * dx + dy * dy <= 1;
}

/** Cette zone est-elle une destination du Vol : une ville déjà visitée, autre que celle où l'on est (jamais un lieu) ? */
export const isFlyDestination = (map: Pick<WorldMapView, 'position'>, area: WorldMapArea & { visited: boolean }): boolean =>
  area.kind === 'town' && area.visited && area.mapId !== map.position?.mapId;

/** Peut-on s'envoler vers cette zone : le Vol est possible et c'en est une destination ? */
export const canFlyTo = (map: Pick<WorldMapView, 'fly' | 'position'>, area: WorldMapArea & { visited: boolean }): boolean =>
  map.fly?.allowed === true && isFlyDestination(map, area);

/** Rectangle englobant une zone. */
export function areaBounds(area: WorldMapArea): { x: number; y: number; width: number; height: number } {
  if (isRectArea(area)) return { x: area.x, y: area.y, width: area.width, height: area.height };
  const xs = area.pixels.map(([x]) => x), ys = area.pixels.map(([, y]) => y);
  const x = Math.min(...xs), y = Math.min(...ys);
  return { x, y, width: Math.max(...xs) - x + 1, height: Math.max(...ys) - y + 1 };
}

/**
 * Point de la zone qui correspond à une position relative dans la carte du jeu (0..1 sur chaque axe ; le centre
 * par défaut). Dans une ville, le point est placé en proportion dans le rectangle ; sur une route, c'est le
 * pixel de la route le plus proche du point placé en proportion dans son rectangle englobant.
 */
export function locateInArea(area: WorldMapArea, relative = { x: 0.5, y: 0.5 }): { x: number; y: number } {
  const bounds = areaBounds(area);
  const target = { x: bounds.x + relative.x * bounds.width, y: bounds.y + relative.y * bounds.height };
  if (isRectArea(area)) return target;
  let best = { x: 0, y: 0, distance: Infinity };
  for (const [px, py] of area.pixels) {
    const distance = Math.hypot(px + 0.5 - target.x, py + 0.5 - target.y);
    if (distance < best.distance) best = { x: px + 0.5, y: py + 0.5, distance };
  }
  return { x: best.x, y: best.y };
}

/** Carte placée la plus proche de `start` en suivant les passages (parcours en largeur). */
export function nearestPlacedMap(start: number, placed: ReadonlySet<number>, links: ReadonlyMap<number, readonly number[]>): number | null {
  const seen = new Set([start]);
  const queue = [start];
  for (let mapId = queue.shift(); mapId !== undefined; mapId = queue.shift()) {
    if (placed.has(mapId)) return mapId;
    for (const next of links.get(mapId) ?? []) {
      if (seen.has(next)) continue;
      seen.add(next);
      queue.push(next);
    }
  }
  return null;
}

/**
 * Où placer sur la carte du monde un joueur qui est dans un bâtiment : sur la carte d'où l'on y entre, à la case
 * du passage (en ressortant des bâtiments imbriqués, par le moins de passages possible) ; null si aucun passage
 * depuis une ville ou une route n'y mène.
 */
export function buildingEntrance(
  building: number,
  maps: ReadonlyMap<number, { kind: MapKind; warps: readonly Warp[] }>,
): { mapId: number; x: number; y: number } | null {
  const seen = new Set([building]);
  const queue = [building];
  for (let mapId = queue.shift(); mapId !== undefined; mapId = queue.shift()) {
    const entrances = [...maps].flatMap(([from, { kind, warps }]) =>
      warps.filter((warp) => warp.toMap === mapId).map((warp) => ({ mapId: from, kind, x: warp.x, y: warp.y })));
    const outside = entrances.find((entrance) => entrance.kind !== 'building');
    if (outside) return { mapId: outside.mapId, x: outside.x, y: outside.y };
    for (const entrance of entrances) {
      if (seen.has(entrance.mapId)) continue;
      seen.add(entrance.mapId);
      queue.push(entrance.mapId);
    }
  }
  return null;
}
